import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiArrowRight, FiPenTool, FiEye, FiHeart, FiBookOpen, FiCalendar } = FiIcons;

const Branding = () => {
  const services = [
    {
      title: "Brand Identity",
      description: "Logo, palette colori, tipografia e sistema visivo coordinato per distinguerti a Napoli",
      icon: FiPenTool,
      features: ["Logo design unico", "Palette colori strategica", "Tipografia brand"]
    },
    {
      title: "Brand Strategy",
      description: "Posizionamento, valori, personalità e tone of voice del brand per connetterti ai clienti",
      icon: FiEye,
      features: ["Posizionamento mercato", "Valori del marchio", "Tone of voice"]
    },
    {
      title: "Brand Experience",
      description: "Applicazioni pratiche del brand su tutti i touchpoint: dal biglietto da visita ai social",
      icon: FiHeart,
      features: ["Biglietti da visita", "Brochure e flyer", "Packaging design"]
    },
    {
      title: "Brand Guidelines",
      description: "Manuale d'uso completo per mantenere la coerenza del brand in ogni comunicazione",
      icon: FiBookOpen,
      features: ["Manuale d'uso brand", "Esempi applicativi", "Linee guida grafiche"]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Branding Napoli – Identità Visiva e Posizionamento | Web Agency Napoli</title>
        <meta name="description" content="Web Agency Napoli: Servizi di branding, naming, logo design e brand identity professionale. Dai forza e personalità al tuo marchio e differenziati dalla concorrenza." />
        <meta name="keywords" content="branding napoli, brand identity napoli, logo design napoli, naming napoli, posizionamento brand napoli" />
        <link rel="canonical" href="https://realizzazionesitiwebnapoli.com/servizi/branding-napoli" />
      </Helmet>

      {/* Hero Section - Team Creativo al Lavoro */}
      <section className="relative min-h-screen bg-napoli-blu text-white overflow-hidden flex items-center">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1920&auto=format&fit=crop" 
            alt="Team Creativo Branding Napoli" 
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          {/* Overlay gradiente per leggibilità testi */}
          <div className="absolute inset-0 bg-gradient-to-r from-napoli-blu/95 via-napoli-blu/80 to-transparent"></div>
        </div>
        
        <div className="relative container mx-auto px-4 py-20 z-20">
          <div className="max-w-4xl mx-auto text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              className="inline-block bg-napoli-accent text-napoli-blu px-4 py-1 rounded-full font-bold text-sm mb-6 shadow-lg"
            >
              CREATIVITÀ & STRATEGIA
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl lg:text-7xl font-poppins font-bold mb-6 text-white drop-shadow-2xl leading-tight"
            >
              Branding Napoli: <br/>
              <span className="text-napoli-accent">Il Tuo Successo</span> inizia qui.
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl lg:text-2xl mb-10 text-white leading-relaxed drop-shadow-lg font-medium max-w-2xl"
            >
              Affidati a un team di designer e strateghi per creare un'identità di marca potente, coerente e capace di dominare il mercato.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Link to="/contatti" className="bg-napoli-accent hover:bg-yellow-400 text-napoli-blu font-bold py-4 px-10 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-xl text-lg">
                Progetta il tuo Brand
              </Link>
              <a href="https://tidycal.com/sitiwebstudio/consulenza-siti-web-napoli" target="_blank" rel="noopener noreferrer" className="bg-white/10 backdrop-blur-md hover:bg-white/20 text-white border border-white/30 font-bold py-4 px-10 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-xl text-lg">
                <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2 inline" /> Prenota Call Gratis
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-5xl font-poppins font-bold text-napoli-blu mb-6">
              Servizi di Branding Professionali
            </h2>
            <div className="w-24 h-1.5 bg-napoli-accent mx-auto mb-8 rounded-full"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-medium">
              Dalla ricerca del nome alla creazione del manuale di marca: un percorso strategico per il tuo posizionamento.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {services.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-napoli-light rounded-2xl p-10 hover:bg-napoli-blu transition-all duration-500 shadow-xl border border-napoli-azzurro/10"
              >
                <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mb-8 shadow-md group-hover:bg-napoli-accent transition-colors duration-500">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-napoli-blu" />
                </div>
                <h3 className="text-2xl font-poppins font-bold text-napoli-blu mb-4 group-hover:text-white transition-colors duration-500">
                  {service.title}
                </h3>
                <p className="text-gray-700 mb-8 leading-relaxed font-medium group-hover:text-white/80 transition-colors duration-500">
                  {service.description}
                </p>
                <ul className="space-y-4">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-napoli-blu font-bold group-hover:text-white transition-colors duration-500">
                      <div className="w-6 h-6 bg-napoli-accent rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-napoli-blu" />
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Focus Team & Strategia */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1531403009284-440f080d1e12?q=80&w=1920&auto=format&fit=crop" 
            alt="Processo Creativo Branding Napoli" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-napoli-blu/85"></div>
        </div>
        
        <div className="relative container mx-auto px-4 text-center z-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl lg:text-6xl font-poppins font-bold mb-8 text-white">
              Pronto a diventare un Brand Iconico?
            </h2>
            <p className="text-2xl mb-12 text-white/90 font-medium max-w-2xl mx-auto">
              Non lasciare il tuo marchio al caso. Costruisci un'identità solida con l'aiuto dei nostri esperti.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contatti" className="bg-napoli-accent hover:bg-yellow-400 text-napoli-blu font-bold py-5 px-12 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-2xl text-xl">
                Contattaci Subito
              </Link>
              <Link to="/portfolio" className="bg-white hover:bg-gray-100 text-napoli-blu font-bold py-5 px-12 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-2xl text-xl">
                Vedi i Nostri Lavori
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Branding;